<template>
    <el-aside width="$store.state.isCollapsed?'64px':'200px'">
        <el-menu :collapse="$store.state.isCollapsed" :router="true" :collapse-transition="false"
            :default-active="router.fullPath">
            <template v-for="item in visibleMenu" :key="item.path || item.group">
                <!-- 一级菜单 -->
                <el-menu-item v-if="!item.children" :index="item.path">
                    <el-icon>
                        <component :is="icons[item.icon]" />
                    </el-icon>
                    <span>{{ item.label }}</span>
                </el-menu-item>
                <!-- 分组菜单 -->
                <el-sub-menu v-else :index="item.group">
                    <template #title>
                        <el-icon>
                            <component :is="icons[item.icon]" />
                        </el-icon>
                        <span>{{ item.label }}</span>
                    </template>
                    <el-menu-item v-for="sub in item.children" :key="sub.path" :index="sub.path">
                        <el-icon>
                            <component :is="icons[sub.icon]" />
                        </el-icon>{{ sub.label }}
                    </el-menu-item>
                </el-sub-menu>
            </template>
        </el-menu>
    </el-aside>
</template>

<script setup>
/* 侧边菜单
 * —— 菜单项照旧，但每一项都要过一遍「权限等级」：没权限的那个等级连菜单都不显示。
 * 具体能不能进还要看路由守卫（router/index.js 里同样用它过滤路由），
 * 接口那层由服务端 app.js 兜底，三边一致。
 */
import {
    HomeFilled, Avatar, HelpFilled, Histogram, Promotion, CirclePlusFilled, List,
    Briefcase, Management, TrendCharts, MagicStick, Pointer, Lock, Calendar
} from '@element-plus/icons-vue';
import { computed } from 'vue';
import { useRoute } from "vue-router";
import { useStore } from 'vuex';
import { canVisit } from '../../util/permission';

// 菜单里写的是图标名字符串，这里得先在映射表里登记，不然 <component :is="icons[item.icon]" /> 找不到就是空白
const icons = { HomeFilled, Avatar, HelpFilled, Histogram, Promotion, CirclePlusFilled, List, Briefcase, Management, TrendCharts, MagicStick, Pointer, Lock, Calendar };

const store = useStore();
const router = useRoute();

// 菜单结构（跟以前一模一样，只是变成数据驱动，好按权限过滤）
const MENU = [
    { path: '/admin/index', label: '首 页', icon: 'HomeFilled' },
    { label: '页面管理', group: '/admin/setting', icon: 'HelpFilled', children: [
        { path: '/admin/setting/page', label: '图片配置', icon: 'CirclePlusFilled' },
        { path: '/admin/setting/title', label: '标题设置', icon: 'Avatar' },
        { path: '/admin/setting/bilibili', label: 'B站登录', icon: 'Avatar' },
        { path: '/admin/setting/live2d', label: 'Live2D 皮套', icon: 'MagicStick' },
        { path: '/admin/setting/cursor', label: '鼠标指针', icon: 'Pointer' }
    ] },
    { label: '用户管理', group: '/admin/user-manage', icon: 'HelpFilled', children: [
        { path: '/admin/user-manage/adduser', label: '添加用户', icon: 'CirclePlusFilled' },
        { path: '/admin/user-manage/listuser', label: '用户列表', icon: 'Avatar' },
        { path: '/admin/user-manage/role', label: '权限管理', icon: 'Lock' }
    ] },
    { path: '/admin/Feedback', label: '反 馈 列 表', icon: 'HomeFilled' },
    { label: '礼 物 管 理', group: '/admin/gift', icon: 'Promotion', children: [
        { path: '/admin/gift/list', label: '舰礼列表', icon: 'List' },
        { path: '/admin/gift/add', label: '添加舰礼', icon: 'Briefcase' }
    ] },
    { label: '积 分 管 理', group: '/admin/integral', icon: 'Histogram', children: [
        { path: '/admin/integral/list', label: '积分列表', icon: 'Management' },
        { path: '/admin/integral/add', label: '添加积分', icon: 'TrendCharts' }
    ] },
    { label: '积 分 商 城', group: '/admin/shop', icon: 'Histogram', children: [
        { path: '/admin/shop/list', label: '商城列表', icon: 'Management' },
        { path: '/admin/shop/add', label: '添加商品', icon: 'TrendCharts' }
    ] },
    { path: '/admin/Exchange', label: '兑 换 信 息', icon: 'HomeFilled' },
    { label: '匿名留言箱', group: '/admin/message', icon: 'Histogram', children: [
        { path: '/admin/message/list', label: '留言箱列表', icon: 'Management' },
        { path: '/admin/message/add', label: '添加留言箱', icon: 'TrendCharts' }
    ] },
    { path: '/admin/live/calendar', label: '直 播 日 历', icon: 'Calendar' },
    { label: '歌 单 管 理', group: '/admin/song', icon: 'Histogram', children: [
        { path: '/admin/song/list', label: '歌单列表', icon: 'Management' },
        { path: '/admin/song/add', label: '添加歌单', icon: 'TrendCharts' },
        { path: '/admin/song/class', label: '歌单分类管理', icon: 'TrendCharts' }
    ] },
    { label: '路 灯 管 理', group: '/admin/lamp', icon: 'Histogram', children: [
        { path: '/admin/lamp/list', label: '路灯列表', icon: 'Management' },
        { path: '/admin/lamp/add', label: '添加路灯', icon: 'TrendCharts' }
    ] },
    { label: '小黑屋管理', group: '/admin/black', icon: 'Histogram', children: [
        { path: '/admin/black/list', label: '小黑屋列表', icon: 'Management' },
        { path: '/admin/black/add', label: '添加小黑屋', icon: 'TrendCharts' }
    ] }
];

// 按权限过滤：一级看自身，分组里只要有一个能进就显示这一组
const visibleMenu = computed(() => {
    const out = [];
    MENU.forEach((item) => {
        if (!item.children) {
            if (canVisit(item.path)) out.push(item);
            return;
        }
        const kids = item.children.filter((c) => canVisit(c.path));
        if (kids.length) out.push(Object.assign({}, item, { children: kids }));
    });
    return out;
});
</script>

<style lang="scss" scoped>
.el-aside {
    height: 100vh;

    .el-menu {
        height: 100vh;
    }
}
</style>
